import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateObject, StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';
import { User } from 'store/user/models';

import { CmsCore, CmsLineDataPlanCollection, CmsMyAccount, CmsPlanSwitch } from 'store/cms/models';
import { AccountSummary, LineSummary, PlanSummary, SummaryLineWithPlan } from 'store/account-summary/models';
import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { planEligibilityDetails } from 'store/plan-eligibility/models';

import { PlanEligibility } from 'store/line/models';
import { LineDataPlan } from 'store/line/models/line-data-plan';
import { StoreAction } from 'store/actions';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';

@Component({
    selector: 'xm-btg-scheduled-switch',
    templateUrl: './btg-scheduled-switch.html',
    styleUrls: ['./btg-scheduled-switch.scss'],
    standalone: false
})
export class XmBTGScheduledSwitchComponent implements OnInit, OnDestroy {
    public pageData: CmsPlanSwitchData;
    public selectedPlanData: CmsDataPlanDetails;
    public isUnlimited: boolean;
    public iconOption: MediaImageOptions;
    public subhead: string;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public inusePlan: LineDataPlan;
    public inusePlanCode: string;
    public inusePlanName: string;
    public inusePlanData: CmsDataPlanDetails;
    public checkmarkIcon: MediaImageOptions;
    public accountSummary: AccountSummary;
    public currentPlan: PlanSummary;
    public lineSummary: LineSummary;
    public currentLine: LineSummary;
    public byTheGigPlanName: string;
    public unlimitedPlanName: string;
    public buttonPromise: Promise<void | StateObject>;
    public user: User;
    public isBusinessUser: boolean;

    private xmStore: XmStore;
    private state: StateService;
    private subscriptions: Subscription[] = [];
    private planCode: string;
    private config: IXMOptions;
    private cimaCore: CimaCore;
 

    constructor(state: StateService, xmStore: XmStore, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore) {
        Object.assign(this, { state, xmStore, config, cimaCore });
    }

    public ngOnInit(): void {

        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.pageData = this.user?.account?.isSegmentFive  || this.user?.account?.isSegmentSix ? result.btgScheduledSwitchPlanSeven : result.btgScheduledSwitch;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccount>(CmsMyAccount), (cmsMyAccount: CmsMyAccount) => {
            this.byTheGigPlanName = cmsMyAccount.plans.byTheGig;
            this.unlimitedPlanName = cmsMyAccount.plans.unlimited;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.checkmarkIcon = {
                mobile: Cloudinary.generateMediaFromCms(result.icons.greenCircleCheck)
            };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            if (this.accountSummary) {
                const lineByKey: SummaryLineWithPlan = this.accountSummary.getLineSummaryWithPlanByKey(this.state.params.line_id);
                this.currentPlan = lineByKey.plan;
                this.lineSummary = accountSummary.getLineByKey(this.state.params.line_id);
            }
        }));

        this.planCode = decodeURIComponent(this.state.params.plan_code);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.selectedPlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.planCode));
            this.iconOption = {
                mobile: Cloudinary.generateMediaFromCms(this.selectedPlanData.miniIcon)
            };
        }));
        if (this.user?.account?.isSegmentFive  || this.user?.account?.isSegmentSix) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<planEligibilityDetails>(planEligibilityDetails), (result: planEligibilityDetails) => {
                this.inusePlanName = Util.createPascalCase(result.currentPlan.planName);
                this.selectedPlanData.pricePerUnit = this.pageData.price;
                this.buildSubhead();
            }));
        }

        if (!this.user?.account?.isSegmentFive || this.user?.account?.isSegmentSix) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
                filters: { lineKey: this.state.params.line_id }
            }), (planEligibility: PlanEligibility) => {
                this.planEligibility = planEligibility;
                this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === this.planCode);
                this.isUnlimited = this.eligiblePlanDetails.isUnlimited;
                this.selectedPlanData.pricePerUnit = this.isUnlimited ? this.eligiblePlanDetails.postSwitch.lineCost.toString() : this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
                this.inusePlanName = this.getinusePlanName();
                this.buildSubhead();
            }));
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public continue(): void {
        this.state.go('account.activity.details', { line_id: this.state.params.line_id });
    }

    public onKeepMyCurrentPlan(): void {
        const postApiAction: string = StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE_VIA_SSM_PROXY;
        this.buttonPromise = this.xmStore.persist(postApiAction, {
            lineKey: this.state.params.line_id,
            planId: this.selectedPlanData.planCode
        }).then(() => {
            this.clearModel();
            let action: string;

            if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user?.account?.isNoLevel) {
                action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
            } else if (this.user?.account?.isSegmentSix && !this.cimaCore.isBusinessUser) {
                action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
            } else if (this.user?.account?.isSegmentFive || this.user?.account?.isSegmentSix) {
                action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
            } else {
                action = StoreAction.GET_ACCOUNT_SUMMARY;
            }

            const accountSummaryPromise: Promise<AccountSummary> = this.xmStore.fetchPromise<AccountSummary>(action);
            let planEligibilityAction: string;

            if (this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && this.accountSummary.isCbmNoLevel) {
                planEligibilityAction = StoreAction.GET_CBM_PLAN_ELIGIBILITY;
            } else if (!this.isBusinessUser && this.accountSummary.level6Customer) {
                planEligibilityAction = StoreAction.GET_PLAN_EIGHT_ELIGIBILITY;
            } else if (this.accountSummary.level5Customer || this.accountSummary.level6Customer) {
                planEligibilityAction = StoreAction.GET_PLAN_ELIGIBILITY;
            }

            if (planEligibilityAction) {
                const planEligilityPromise: Promise<planEligibilityDetails> = this.xmStore.fetchPromise<planEligibilityDetails>(
                    planEligibilityAction,
                    { params: { lineId: this.state.params.line_id } }
                );

                this.buttonPromise = Promise.all([accountSummaryPromise, planEligilityPromise]).then(() => {
                    this.state.go('account.activity.details', { line_id: this.state.params.line_id });
                });
            } else {
                this.buttonPromise = Promise.all([accountSummaryPromise]).then(() => {
                    this.state.go('account.activity.details', { line_id: this.state.params.line_id });
                });
            }
        }).catch(() => {
            this.state.go('account.activity.details', { line_id: this.state.params.line_id });
        });
    }

    private clearModel(): void {
        let action: string;

        if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user?.account?.isNoLevel) {
            action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
        } else if (this.user?.account?.isSegmentSix && !this.cimaCore.isBusinessUser) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
        } else if (this.user?.account?.isSegmentFive || this.user?.account?.isSegmentSix) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
        } else {
            action = StoreAction.GET_ACCOUNT_SUMMARY;
        }

        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_LINE_LEVEL_PLAN_ELIGIBILITY },
            { action }
        ];

        this.xmStore.clearApis(clearApisList);
    }

    private buildSubhead(): void {
        this.subhead = this.pageData.subhead.replace(/\[NEW_PLAN_NAME\]/g, this.selectedPlanData.planName);
        this.subhead = this.subhead.replace(/\[CURRENT_PLAN_NAME\]/g, this.inusePlanName);

    }

    private getinusePlanName(): string {
        if (this.isUnlimited && this.currentPlan.isUnlimited) {
            this.inusePlan = this.planEligibility.plans.find((plan: LineDataPlan) => plan.isIneligibleInUse);
            this.inusePlanCode = this.inusePlan.planCode;

       
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
                this.inusePlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.inusePlanCode));
            }));
            
            return this.inusePlanData.planName;
        }
        if (!this.isUnlimited) {
            this.currentLine = this.accountSummary.getLineByKey(this.state.params.line_id);
            
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
                this.inusePlanData = result.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(this.currentLine.planCode));
            }));
            
            return this.inusePlanData.planName;
        } else {
            
            return this.byTheGigPlanName;
        }
        
    }
}
